import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Define the type for navigation items
interface NavItem {
  path: string;
  label: string;
  className?: string;
  icon?: React.ReactNode;
}

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isEmailLinked, setIsEmailLinked] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  // Simulate checking login status - replace with actual auth check
  useEffect(() => {
    // For demo, we'll check the URL - in a real app this would check authentication state
    const checkLoginStatus = () => {
      // If we're on dashboard or paths that require auth, consider the user logged in
      const loggedInPaths = ['/dashboard', '/email-link', '/client-scores'];
      setIsLoggedIn(
        loggedInPaths.some((path) => location.pathname.startsWith(path))
      );

      // For demo, we'll consider email linked if on client-scores or dashboard
      setIsEmailLinked(
        location.pathname === '/client-scores' ||
          location.pathname === '/dashboard'
      );
    };

    checkLoginStatus();
  }, [location.pathname]);

  // Email linked badge
  const emailLinkedIcon = (
    <span className="inline-flex items-center justify-center ml-1">
      <span className="h-2 w-2 rounded-full bg-green-400"></span>
    </span>
  );

  // Define navigation items based on login status
  const getNavItems = (): NavItem[] => {
    // Items always visible
    const commonItems: NavItem[] = [{ path: '/', label: 'Home' }];

    // Items only for logged in users
    const loggedInItems: NavItem[] = [
      { path: '/dashboard', label: 'Dashboard' },
      {
        path: '/email-link',
        label: isEmailLinked ? 'Email' : 'Link Email',
        className: isEmailLinked ? 'text-green-300 font-semibold' : '',
        icon: isEmailLinked ? emailLinkedIcon : undefined,
      },
      { path: '/client-scores', label: 'Client Scores' },
    ];

    // Items only for guests
    const guestItems: NavItem[] = [
      { path: '/login', label: 'Login' },
      { path: '/register', label: 'Register' },
    ];

    return [...commonItems, ...(isLoggedIn ? loggedInItems : guestItems)];
  };

  const navItems = getNavItems();

  return (
    <nav className="bg-gradient-to-r from-blue-600 to-blue-800 shadow-lg">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link
            to="/"
            className="text-2xl font-bold text-white flex items-center">
            <span className="bg-white text-blue-600 rounded-full p-1 mr-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </span>
            Done Deal
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center
                  ${item.className || ''}
                  ${
                    isActive(item.path)
                      ? 'bg-white text-blue-600'
                      : 'text-white hover:bg-blue-500 hover:text-white'
                  }`}>
                {item.label}
                {item.icon}
              </Link>
            ))}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-white hover:text-gray-200 focus:outline-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    isMenuOpen
                      ? 'M6 18L18 6M6 6l12 12'
                      : 'M4 6h16M4 12h16M4 18h16'
                  }
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-3 space-y-1 pb-3 pt-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block px-3 py-2 rounded-md text-base font-medium flex items-center
                  ${item.className || ''}
                  ${
                    isActive(item.path)
                      ? 'bg-white text-blue-600'
                      : 'text-white hover:bg-blue-500 hover:text-white'
                  }`}
                onClick={() => setIsMenuOpen(false)}>
                {item.label}
                {item.icon}
              </Link>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
